import Button from '@rio-cloud/rio-uikit/Button';

import type { PickupPreset, Signal } from '../../services/scmMockApi';
import {
    chainFrozen,
    chainPresets,
    datePresets,
    demoDates,
    demoPickup,
    type EventChain,
    pickupPresets,
    publicationKey,
    publishedRequests,
    publishedTransports,
    requestNeedsPublication,
} from './demoState';

type SignalProps = {
    chain: EventChain;
    busy: boolean;
    change: (
        patch: Partial<Pick<EventChain, 'datePreset' | 'count' | 'requestCount' | 'pickupPreset' | 'requestStatus'>>
    ) => void;
    publish: (signal: Signal, all?: boolean, pickup?: PickupPreset) => void;
    selectTab: (tab: Signal) => void;
};
const shortDate = (date: string) =>
    new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
        new Date(`${date}T12:00:00Z`)
    );

const ScheduleChoices = ({ chain, change }: Pick<SignalProps, 'chain' | 'change'>) => (
    <fieldset className='scm-demo-schedules'>
        <legend>
            Requested schedule <span>Europe/Berlin</span>
        </legend>
        <div className='scm-demo-schedule-options'>
            {datePresets.map(preset => {
                const dates = demoDates({ ...chain, datePreset: preset.value });
                return (
                    <button
                        type='button'
                        key={preset.value}
                        className='scm-demo-schedule'
                        aria-pressed={chain.datePreset === preset.value}
                        disabled={chainFrozen(chain)}
                        onClick={() => change({ datePreset: preset.value })}
                    >
                        <span className='scm-demo-choice-title'>
                            {preset.label}
                            <span aria-hidden='true'>{chain.datePreset === preset.value ? '✓' : ''}</span>
                        </span>
                        <span className='scm-demo-route'>
                            <span>
                                <small>Pickup · 09:00</small>
                                <strong>{shortDate(dates.pickup)}</strong>
                            </span>
                            <span
                                className='scm-demo-route-line'
                                role='img'
                                aria-label={
                                    preset.value === 'delivery_two_days_later'
                                        ? '3 days to delivery'
                                        : '1 day to delivery'
                                }
                            >
                                <span>{preset.value === 'delivery_two_days_later' ? '3 days' : '1 day'}</span>
                            </span>
                            <span>
                                <small>Delivery</small>
                                <strong>{shortDate(dates.delivery)}</strong>
                            </span>
                        </span>
                    </button>
                );
            })}
        </div>
    </fieldset>
);
export const ChainPresets = ({ chain, change }: Pick<SignalProps, 'chain' | 'change'>) => (
    <fieldset className='scm-demo-presets' aria-label='Event chain preset'>
        <legend>Event chain preset</legend>
        <div>
            {chainPresets.map(preset => (
                <Button
                    key={preset.label}
                    aria-pressed={preset.count === chain.count && preset.requestCount === chain.requestCount}
                    disabled={chainFrozen(chain)}
                    onClick={() => change({ count: preset.count, requestCount: preset.requestCount })}
                >
                    {preset.label}
                </Button>
            ))}
        </div>
        <p className='scm-demo-hint'>Publish either side first. Counterparts can be added later, one at a time.</p>
    </fieldset>
);
const RequestSignal = ({ chain, busy, change, publish }: SignalProps) => {
    const published = publishedRequests(chain);
    const nextIndex = Array.from({ length: chain.requestCount }, (_, i) => i + 1).find(index =>
        requestNeedsPublication(chain, index)
    );
    const publication = nextIndex ? chain.publications[publicationKey('request', nextIndex)] : undefined;
    return (
        <div className='scm-demo-panel-layout'>
            <ScheduleChoices chain={chain} change={change} />
            <div className='scm-demo-command'>
                <span className='scm-demo-label'>
                    {published} of {chain.requestCount} requests published
                </span>
                <label>
                    Request state
                    <select
                        aria-label='Request state'
                        className='form-control'
                        value={chain.requestStatus ?? 'SENT'}
                        disabled={busy || (!!publication && !publication.receipt)}
                        onChange={event => change({ requestStatus: event.target.value as EventChain['requestStatus'] })}
                    >
                        {['DRAFT', 'SCHEDULED', 'SENT', 'MISSED'].map(status => (
                            <option key={status}>{status}</option>
                        ))}
                    </select>
                </label>
                <Button
                    bsStyle={Button.PRIMARY}
                    disabled={busy || nextIndex === undefined}
                    onClick={() => publish('request')}
                >
                    {nextIndex === undefined
                        ? chain.requestCount === 1
                            ? 'Request published'
                            : 'All requests published'
                        : publication?.receipt
                          ? chain.requestCount === 1
                              ? 'Update request'
                              : 'Update next request'
                          : publication
                            ? 'Retry request'
                            : chain.requestCount === 1
                              ? 'Create request'
                              : 'Create next request'}
                </Button>
                {chain.requestCount > 1 && nextIndex !== undefined && (
                    <Button disabled={busy} onClick={() => publish('request', true)}>
                        Create all remaining requests
                    </Button>
                )}
                {Array.from({ length: chain.requestCount }, (_, i) => i + 1).map(index => {
                    const entry = chain.publications[publicationKey('request', index)];
                    return (
                        <div key={index} className='scm-demo-publication'>
                            <strong>Request {index}</strong>
                            <span>
                                {entry?.receipt ? 'Published' : entry?.error ? 'Retry needed' : 'Not published'}
                            </span>
                            {entry?.error && <p role='alert'>{entry.error}</p>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
const TransportsSignal = ({ chain, busy, change, publish, selectTab }: SignalProps) => {
    const published = publishedTransports(chain);
    const nextIndex = Array.from({ length: chain.count }, (_, i) => i + 1).find(
        index => !chain.publications[publicationKey('transports', index)]?.receipt
    );
    const pendingCommand = nextIndex ? chain.publications[publicationKey('transports', nextIndex)]?.command : undefined;
    const preset = pendingCommand?.pickup_preset ?? chain.pickupPreset;
    const dates = demoDates(chain);
    const pickup = demoPickup(chain, preset);
    const pending = !!pendingCommand;
    return (
        <>
            <div className='scm-demo-panel-layout'>
                <div className='scm-demo-pickup-editor'>
                    {published < chain.count ? (
                        <>
                            <fieldset className='scm-demo-pickup-options'>
                                <legend>
                                    Transport {nextIndex} pickup <span>Europe/Berlin</span>
                                </legend>
                                <div>
                                    {pickupPresets.map(value => (
                                        <button
                                            type='button'
                                            key={value.value}
                                            aria-pressed={preset === value.value}
                                            disabled={busy || pending}
                                            onClick={() => change({ pickupPreset: value.value })}
                                        >
                                            {value.shortLabel}
                                        </button>
                                    ))}
                                </div>
                            </fieldset>
                            <div className='scm-demo-pickup-preview'>
                                <div>
                                    <span className='scm-demo-label'>Requested pickup</span>
                                    <strong>
                                        {shortDate(dates.pickup)} <span>09:00</span>
                                    </strong>
                                </div>
                                <span className='scm-demo-comparison' aria-hidden='true'>
                                    →
                                </span>
                                <div
                                    className={
                                        preset === 'matching'
                                            ? 'scm-demo-window'
                                            : 'scm-demo-window scm-demo-window-shifted'
                                    }
                                >
                                    <span className='scm-demo-label'>Confirmed window</span>
                                    <strong>
                                        {shortDate(pickup.date)}{' '}
                                        <span>
                                            {pickup.start}–{pickup.end}
                                        </span>
                                    </strong>
                                </div>
                                {!chainFrozen(chain) && (
                                    <Button variant={Button.VARIANT_LINK} onClick={() => selectTab('request')}>
                                        Change schedule
                                    </Button>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className='scm-demo-complete'>
                            <span className='scm-demo-complete-icon' aria-hidden='true'>
                                ✓
                            </span>
                            <div>
                                <strong>All transports published</strong>
                                <span>
                                    {shortDate(dates.pickup)} pickup → {shortDate(dates.delivery)} delivery
                                </span>
                            </div>
                        </div>
                    )}
                </div>
                <div className='scm-demo-command'>
                    {published < chain.count && (
                        <div className='scm-demo-actions'>
                            <Button
                                bsStyle={Button.PRIMARY}
                                disabled={busy}
                                onClick={() => publish('transports', false, preset)}
                            >
                                {pending ? 'Retry transport' : 'Create next transport'}
                            </Button>
                            {chain.count > 1 && (
                                <Button disabled={busy} onClick={() => publish('transports', true, preset)}>
                                    Create all remaining
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className='scm-demo-progress'>
                <span className='scm-demo-label'>
                    {published} of {chain.count} published
                </span>
                <div className='scm-demo-publications'>
                    {Array.from({ length: chain.count }, (_, i) => i + 1).map(index => {
                        const publication = chain.publications[publicationKey('transports', index)];
                        const status = publication?.receipt
                            ? 'Published'
                            : publication?.error
                              ? 'Retry needed'
                              : publication
                                ? 'Pending'
                                : 'Ready';
                        return (
                            <div className='scm-demo-publication' key={index} data-status={status}>
                                <span className='scm-demo-unit' aria-hidden='true'>
                                    {publication?.receipt ? '✓' : index}
                                </span>
                                <div>
                                    <strong>Transport {index}</strong>
                                    <span>
                                        {publication
                                            ? pickupPresets.find(
                                                  value => value.value === publication.command.pickup_preset
                                              )?.shortLabel
                                            : 'Not published'}
                                    </span>
                                </div>
                                <span className='scm-demo-publication-status'>{status}</span>
                            </div>
                        );
                    })}
                </div>
                {Object.entries(chain.publications)
                    .filter(([key, publication]) => key.startsWith('transport:') && publication.error)
                    .map(([key, publication]) => (
                        <p role='alert' key={key}>
                            {publication.error}
                        </p>
                    ))}
            </div>
        </>
    );
};
export const demoSignals = [
    { id: 'request', label: 'Transport requests', component: RequestSignal },
    { id: 'transports', label: 'Transports', component: TransportsSignal },
] as const;
